import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { generateChallenges } from "../utils/enhancedGuiltAlgorithm";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";

const Challenges = ({ purchases, userProfile, theme }) => {
  const [challenges, setChallenges] = useState([]);
  const [activeChallenges, setActiveChallenges] = useState(() => {
    const saved = localStorage.getItem("guiltGaugeChallenges");
    return saved ? JSON.parse(saved) : [];
  });
  const [completedChallenges, setCompletedChallenges] = useState(() => {
    const saved = localStorage.getItem("guiltGaugeCompletedChallenges");
    return saved ? JSON.parse(saved) : [];
  });
  const [showConfetti, setShowConfetti] = useState(false);
  const { width, height } = useWindowSize();
  
  // Generate challenges based on purchase history
  useEffect(() => {
    const generatedChallenges = generateChallenges(purchases, userProfile);
    setChallenges(generatedChallenges);
  }, [purchases, userProfile]);
  
  // Save challenges to localStorage
  useEffect(() => {
    localStorage.setItem("guiltGaugeChallenges", JSON.stringify(activeChallenges));
    localStorage.setItem("guiltGaugeCompletedChallenges", JSON.stringify(completedChallenges));
  }, [activeChallenges, completedChallenges]);
  
  // Accept a challenge
  const acceptChallenge = (challenge) => {
    setActiveChallenges([...activeChallenges, {
      ...challenge,
      startDate: new Date().toISOString(),
      progress: 0
    }]);
  };
  
  // Complete a challenge
  const completeChallenge = (challengeId) => {
    const challenge = activeChallenges.find(c => c.id === challengeId);
    if (challenge) {
      // Remove from active challenges
      setActiveChallenges(activeChallenges.filter(c => c.id !== challengeId));
      
      // Add to completed challenges
      setCompletedChallenges([...completedChallenges, {
        ...challenge,
        completedDate: new Date().toISOString()
      }]);
      
      // Show confetti
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
    }
  };
  
  // Update challenge progress
  const updateChallengeProgress = (challengeId, progress) => {
    setActiveChallenges(activeChallenges.map(challenge => 
      challenge.id === challengeId 
        ? { ...challenge, progress: Math.min(100, progress) } 
        : challenge
    ));
    
    // Auto-complete if progress reaches 100%
    if (progress >= 100) {
      completeChallenge(challengeId);
    }
  };
  
  // Abandon a challenge
  const abandonChallenge = (challengeId) => {
    setActiveChallenges(activeChallenges.filter(c => c.id !== challengeId));
  };
  
  // Get difficulty badge styling
  const getDifficultyBadge = (difficulty) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-100 text-green-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      case "hard":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };
  
  // Available challenges (not active or completed)
  const availableChallenges = challenges.filter(challenge => 
    !activeChallenges.some(c => c.id === challenge.id) &&
    !completedChallenges.some(c => c.id === challenge.id)
  );
  
  return (
    <motion.div
      className="bg-white rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {showConfetti && <Confetti width={width} height={height} recycle={false} />}
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Shopping Challenges</h2>
        <div className="text-sm text-gray-500">
          {completedChallenges.length} completed
        </div>
      </div>
      
      {/* Active Challenges */}
      {activeChallenges.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold mb-3 text-lg">Active Challenges</h3>
          <div className="space-y-3">
            {activeChallenges.map((challenge) => (
              <div key={challenge.id} className="bg-gray-50 rounded-xl p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-bold">{challenge.title}</h4>
                    <p className="text-sm text-gray-600">{challenge.description}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyBadge(challenge.difficulty)}`}>
                    {challenge.difficulty}
                  </span>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2.5 my-3">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{ width: `${challenge.progress}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between items-center text-xs text-gray-500">
                  <div>Started: {formatDate(challenge.startDate)}</div>
                  <div>{challenge.progress}% complete</div>
                </div>
                
                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => updateChallengeProgress(challenge.id, challenge.progress + 25)}
                    className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                  >
                    Update Progress
                  </button>
                  <button
                    onClick={() => completeChallenge(challenge.id)}
                    className={`flex-1 px-3 py-2 text-sm text-white rounded-lg ${buttonStyles[theme]}`}
                  >
                    Complete
                  </button>
                  <button
                    onClick={() => abandonChallenge(challenge.id)}
                    className="px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Available Challenges */}
      {availableChallenges.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold mb-3 text-lg">Available Challenges</h3>
          <div className="space-y-3">
            {availableChallenges.slice(0, 3).map((challenge) => (
              <div key={challenge.id} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-bold dark:text-white">{challenge.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{challenge.description}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyBadge(challenge.difficulty)}`}>
                    {challenge.difficulty}
                  </span>
                </div>
                
                <div className="mt-2 text-sm">
                  <span className="font-medium dark:text-white">Reward:</span> {challenge.reward}
                </div>
                
                <div className="mt-4">
                  {challenge.id === "shop-mindfully" ? (
                    <motion.button
                      onClick={() => window.location.href = '/shop'}
                      className={`px-4 py-2 rounded-xl text-white text-sm ${buttonStyles[theme]}`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Browse Mindful Alternatives
                    </motion.button>
                  ) : (
                    <motion.button
                      onClick={() => acceptChallenge(challenge)}
                      className={`px-4 py-2 rounded-xl text-white text-sm ${buttonStyles[theme]}`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Accept Challenge
                    </motion.button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Completed Challenges */}
      {completedChallenges.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 text-lg">Completed Challenges</h3>
          <div className="space-y-2">
            {completedChallenges.slice(0, 5).map((challenge) => (
              <div key={challenge.id} className="bg-gray-50 rounded-xl p-3 flex justify-between items-center">
                <div>
                  <h4 className="font-medium">{challenge.title}</h4>
                  <p className="text-xs text-gray-500">
                    Completed on {formatDate(challenge.completedDate)}
                  </p>
                </div>
                <div className="text-2xl">🏆</div>
              </div>
            ))}
            
            {completedChallenges.length > 5 && (
              <div className="text-center text-sm text-gray-500">
                +{completedChallenges.length - 5} more completed challenges
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* No challenges available */}
      {challenges.length === 0 && (
        <div className="text-center p-8">
          <div className="text-5xl mb-3">🎯</div>
          <h3 className="text-xl font-bold mb-2">Challenges coming soon!</h3>
          <p className="text-gray-600">
            Log a few more purchases to unlock personalized shopping challenges.
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default Challenges;
